// START IMPORTS
// Context imports
import { AppConfig } from '@ltvco/refresh-lib/ctx';
// Hooks imports
import { useReport } from '@ltvco/refresh-lib/hooks';
// Unclaimed money imports
import {
  UnclaimedMoneyReport,
  UnclaimedMoneyReportLoading,
  UnclaimedMoneyResults,
} from '@ltvco/refresh-lib/unclaimedMoney';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
// Core lib v2 imports
import { ReportNullState } from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { useQueryParams } from 'utils/useQueryParams';

// Asset imports
import imgRobotMonitor from '../../images/img-check-icon.svg';
import imgUnclaimedMoney from '../../images/img_unclaimed_money.svg';
import imgRobotLoading from '../../images/img_unclaimed_money_loading.svg';
import imgRobotNoResults from '../../images/img_unclaimed_money_no_results.svg';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface UnclaimedMoneyReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

export function UnclaimedMoneyPage({ permalink }: UnclaimedMoneyReportProps) {
  // START HOOKS_AND_QUERIES
  const queryParams = useQueryParams();
  const queryResult = useReport(permalink);
  const { hash } = useLocation();
  const { trackEvent } = useContext(AppConfig);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [stateList, setStateList] = useState<string[]>([]);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const zeroed = isZeroed(queryResult);
  const report = new UnclaimedMoneyReport(queryResult?.data);
  const search_data = report.data.meta?.search_data;

  const firstName =
    queryParams.get('first_name') || search_data?.first_name || '';
  const lastName = queryParams.get('last_name') || search_data?.last_name || '';
  const state = queryParams.get('state') || search_data?.state || '';
  const states = queryParams.get('states')?.split(',') || [];
  const multiStateSearch = !!queryParams.get('multi_state_search');

  useScrollToSectionOnNavigate(hash, queryResult);
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (stateList.length) {
      return;
    }

    if (multiStateSearch && states.length) {
      setStateList(states);
    } else if (state) {
      setStateList([state]);
    }
  }, [multiStateSearch, state, states, stateList.length]);

  trackEvent('view full report', 'view', 'unclaimed money search');
  // END EFFECTS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return (
      <UnclaimedMoneyReportLoading
        variation="query"
        topImage={imgUnclaimedMoney}
        firstName={firstName}
        lastName={lastName}
        stateList={stateList}
      />
    );
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  if (!report) {
    return (
      <UnclaimedMoneyReportLoading
        variation="query"
        topImage={imgUnclaimedMoney}
        firstName={firstName}
        lastName={lastName}
        stateList={stateList}
      />
    );
  }
  // END EARLY_RETURNS

  // START RENDER
  return (
    <>
      <UnclaimedMoneyResults
        permalink={permalink}
        isLoading={queryResult.isLoading}
        loadingImage={imgRobotLoading}
        noResultsImage={imgRobotNoResults}
        mainReport={report}
        topImage={imgUnclaimedMoney}
        multiStateSearch={multiStateSearch}
        firstName={firstName}
        lastName={lastName}
        state={state}
        modalImage={imgRobotMonitor}
        stateList={stateList}
      />
    </>
  );
  // END RENDER
}
